import React, { useEffect, useState } from "react";
import ListReductionMeasuresDesign from "./ListReductionMeasures.design";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ReductionMeasureListModel } from "object-models/ReductionMeasures";

import { useParams } from "react-router-dom";
import {
  _deleteReductionMeasure,
  _downloadReductionMeasuresTemplate,
  _getReductionMeasureList,
} from "middlewares/ReductionMeasure/ReductionMeasureApi";
import ViewReductionMeasuresLayout from "../ViewReductionMeasures/ViewReductionMeasuresLayout";
import Spinner from "components/Spinner/Spinner";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AlertDialog from "components/AlertDialog";
import { ConfirmationMessages, UserRoles } from "constants/constant";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import SnackBar from "components/SnackBar";
import { useAppState } from "context/appState.context";
import { Tooltip } from "@mui/material";
import FormDialog from "components/Dialog/modal-popup";
import UploadMeasure from "../UploadMeasure";
import { Utilities } from "utilities/utilities";
import { getFriendlyErrorMessage, reportUiError } from "services/ui-error-reporter";
import { _getLocation } from "middlewares/LocationApi/location";

/**
 * Props for the ListReductionMeasures component.
 * @typedef {Object} ListReductionMeasuresProps
 * @property {Function} onAddMeasureClick - Callback function triggered when the add measure button is clicked.
 * @property {Function} onEditMeasureClick - Callback function triggered when a measure is to be edited.
 * @property {boolean} refreshView - Flag indicating whether the view needs to be refreshed.
 */
interface ListReductionMeasuresProps {
  onAddMeasureClick: () => void;
  onEditMeasureClick: (selectedMeasureId: number) => void;
  refreshView: boolean;
}

/**
 * ListReductionMeasures component displays a list of reduction measures and handles various actions like viewing, editing, and deleting measures.
 *
 * @param {ListReductionMeasuresProps} props - The props for the component.
 * @returns {JSX.Element} The rendered component.
 */
export default function ListReductionMeasures(
  props: Readonly<ListReductionMeasuresProps>
) {
  const { LocationId } = useParams();
  const { userRole } = useAppState();

  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const defaultReductionMeasurelist: Array<ReductionMeasureListModel> = [];
  const [reductionMeasureList, setReductionMeasureList] = useState(
    defaultReductionMeasurelist
  );
  const [isTableView, setIsTableView] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [reductionMeasureId, setReductionMeasureId] = useState(0);
  const [reductionMeasureName, setReductionMeasureName] = useState("");
  const [success, setSuccess] = useState<AlertsProps>({
    show: false,
    severity: "info",
    message: "",
  });
  const [selectedReductionMeasureId, setSelectedReductionMeasureId] =
    useState(0);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const parsedLocationId = LocationId !== undefined ? Number(LocationId) : NaN;
  const isLocationIdValid = !isNaN(parsedLocationId) && parsedLocationId > 0;
  const [growthEndYear, setGrowthEndYear] = useState<number | null>(null);

  /**
   * Handles the view action for a reduction measure.
   *
   * @param {number} id - The ID of the reduction measure to view.
   */
  const handleView = (id: any) => {
    setSelectedReductionMeasureId(id);
    setIsTableView(false);
  };

  /**
   * Handles the edit action for a reduction measure.
   *
   * @param {number} id - The ID of the reduction measure to edit.
   */
  const handleEdit = (id: any) => {
    setSelectedReductionMeasureId(id);
    setOpenEditModal(true);
  };

  /**
   * Hides the edit dialog.
   */
  const hideDialogHandler = () => setOpenEditModal(false);

  /**
   * Handles the back action to return to the table view.
   */
  const OnBackClick = () => {
    setSelectedReductionMeasureId(0);
    setIsTableView(true);
  };

  /**
   * Fetches the list of reduction measures based on the location ID.
   */
  const getReductionMeasureListByLocationId = async () => {
    try {
      setLoading(true);
      const res = await _getReductionMeasureList(+(LocationId ?? "0"));
      if (res) {
        setReductionMeasureList(res);
        setLoading(false);
      }
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const getGrowthEndYearByLocationId = async () => {
    try {
      const location = await _getLocation(Number(LocationId));
      const parsedGrowthEndYear = Number(location?.targetYear);
      setGrowthEndYear(Number.isFinite(parsedGrowthEndYear) ? parsedGrowthEndYear : null);
    } catch {
      setGrowthEndYear(null);
    }
  };

  /**
   * Opens the delete confirmation dialog.
   */
  const openDialog = () => {
    setOpenModal(true);
  };

  /**
   * Closes the delete confirmation dialog.
   */
  const handleClose = () => {
    setOpenModal(false);
  };

  /**
   * Handles the snackbar close action.
   *
   * @param {AlertsProps} state - The state to set for the snackbar.
   */
  const handleCloseSnackBar = (state: AlertsProps) => {
    setSuccess(state);
  };

  const isRefreshEditClose = () => {
    setLoading(true);
    getReductionMeasureListByLocationId();
  };

  /**
   * Deletes the selected reduction measure.
   */
  const deleteReductionMeasure = async () => {
    try {
      const res = await _deleteReductionMeasure(reductionMeasureId);
      if (res) {
        getReductionMeasureListByLocationId();
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  /**
   * Confirms the deletion of the reduction measure.
   */
  const handleConfirm = () => {
    deleteReductionMeasure();
    handleClose();
    handleCloseSnackBar({
      show: true,
      severity: "success",
      message: "Deleted Successfully !",
    });
  };

  /**
   * Handles the delete action for a reduction measure.
   *
   * @param {number} reductionMeasureId - The ID of the reduction measure to delete.
   * @param {string} reductionMeasureName - The name of the reduction measure to delete.
   */
  const handleDeleteClick = (
    reductionMeasureId: number,
    reductionMeasureName: string
  ) => {
    openDialog();
    setReductionMeasureId(reductionMeasureId);
    setReductionMeasureName(reductionMeasureName);
  };

  function onUploadMeasureClick() {
    setDialogOpen(true);
  }

  const onDownloadTemplateClick = async () => {
    if (!isLocationIdValid) {
      setSuccess({
        show: true,
        severity: "error",
        message: "Invalid or missing Location ID. Cannot download template.",
      });
      return;
    }

    try {
      setIsDownloadingTemplate(true);
      const { blob, fileName } = await _downloadReductionMeasuresTemplate(
        parsedLocationId
      );

      if (blob) {
        const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
        Utilities.triggerFileDownload(
          blob,
          fileName ?? `ReductionMeasures_${parsedLocationId}_${today}.xlsx`
        );
      }
    } catch (err) {
      const message = getFriendlyErrorMessage(
        err,
        "Failed to download template. Please try again."
      );
      setSuccess({
        show: true,
        severity: "error",
        message,
      });
      reportUiError(err, {
        feature: "ReductionMeasures",
        action: "download reduction measure template",
        component: "ListReductionMeasures",
        route: typeof window !== "undefined" ? window.location.pathname : undefined,
        extra: {
          locationId: parsedLocationId,
        },
      });
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const hideDialogHandlernew = () => setDialogOpen(false);

  useEffect(() => {
    setLoading(true);
    getReductionMeasureListByLocationId();
    getGrowthEndYearByLocationId();
  }, []);

  useEffect(() => {
    if (props.refreshView === true) {
      setLoading(true);
      getReductionMeasureListByLocationId();
    }
  }, [props.refreshView]);

  const highestMeasureEndYear = reductionMeasureList.reduce<number | null>(
    (highestYear, measure) => {
      const measureEndYear = Number(measure?.endYear);
      if (!Number.isFinite(measureEndYear)) {
        return highestYear;
      }

      return highestYear === null
        ? measureEndYear
        : Math.max(highestYear, measureEndYear);
    },
    null
  );

  const columns = [
    {
      field: "measureNumber",
      headerClassName: "table-header",
      headerName: "Measure ID",
      width: 140,
      minWidth: 140,
      renderCell: (params: any) => (
        <div style={{ textAlign: "center", width: "100%" }}>{params.value}</div>
      ),
    },
    {
      field: "reductionMeasureName",
      headerClassName: "table-header",
      headerName: "Measure Name",
      flex: 0.6,
      minWidth: 130,
      renderCell: (params: any) => (
        <Tooltip title={params.value ?? ""} disableHoverListener={params.value?.length <= 25}>
          <div
            style={{
              textAlign: "left",
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value}
          </div>
        </Tooltip>
      ),
    },
    {
      field: "measureCategory",
      headerClassName: "table-header",
      headerName: "Measure Category",
      flex: 0.25,
      minWidth: 220,
    },
    {
      field: "measureType",
      headerClassName: "table-header",
      headerName: "Measure Type",
      flex: 0.25,
      minWidth: 120,
    },
    {
      field: "measureStage",
      headerClassName: "table-header",
      headerName: "Stage",
      flex: 0.25,
      minWidth: 150,
    },
    {
      field: "businessUnit",
      headerClassName: "table-header",
      headerName: "Business Unit",
      flex: 0.25,
      minWidth: 120,
    },
    {
      field: "measureDescription",
      headerClassName: "table-header",
      headerName: "Description",
      flex: 0.5,
      minWidth: 150,
    },
    {
      field: "actions",
      headerClassName: "table-header",
      headerName: "Actions",
      width: 130,
      minWidth: 130,
      renderCell: (params: any) => (
        <div>
          <span
            title="View Measure"
            style={{ cursor: "pointer", paddingRight: 5 }}
          >
            <VisibilityIcon
              data-testid={"view" + params.row.id}
              style={{ color: "#e4610f" }}
              onClick={() => handleView(params.row.reductionMeasureId)}
            />
          </span>
          {userRole !== UserRoles.Viewers && (
            <>
              <span
                title="Edit Measure"
                style={{ cursor: "pointer", paddingRight: 5 }}
              >
                <EditIcon
                  style={{ color: "#e4610f" }}
                  onClick={() => handleEdit(params.row.reductionMeasureId)}
                  data-testid={"editiconTestId" + params.row.id}
                />
              </span>
              <span title="Delete Measure" style={{ cursor: "pointer" }}>
                <DeleteOutlineIcon
                  onClick={() =>
                    handleDeleteClick(
                      params.row.reductionMeasureId,
                      params.row.reductionMeasureName
                    )
                  }
                  data-testid={"deleteIconTestId" + params.row.id}
                  style={{
                    color: "#e4610f",
                    cursor: "pointer",
                  }}
                />
              </span>
            </>
          )}
        </div>
      ),
      sortable: false,
      disableColumnMenu: true,
    },
  ];

  let renderedComponent;

  const shouldShowDashboardYearConstraint =
    growthEndYear !== null &&
    highestMeasureEndYear !== null &&
    highestMeasureEndYear > growthEndYear;

  if (isTableView) {
    if (!loading) {
      renderedComponent = (
        <ListReductionMeasuresDesign
          onAddMeasureClick={props.onAddMeasureClick}
          openEditModal={openEditModal}
          selectedReductionMeasureId={selectedReductionMeasureId}
          columns={columns}
          reductionMeasureList={reductionMeasureList}
          handleCloseSnackBar={handleCloseSnackBar}
          hideDialogHandler={hideDialogHandler}
          isRefreshEditClose={isRefreshEditClose}
          onUploadMeasureClick={onUploadMeasureClick}
          onDownloadTemplateClick={onDownloadTemplateClick}
          isDownloadingTemplate={isDownloadingTemplate}
          disableDownloadTemplate={!isLocationIdValid}
          showDashboardYearConstraint={shouldShowDashboardYearConstraint}
        />
      );
    } else {
      renderedComponent = <Spinner size={80} data-testid="spinner" />;
    }
  } else {
    renderedComponent = (
      <ViewReductionMeasuresLayout
        onEditMeasureClick={props.onEditMeasureClick}
        selectedReductionMeasureId={selectedReductionMeasureId}
        reductionMeasureIdList={reductionMeasureList.map(
          (rm) => rm.reductionMeasureId
        )}
        onBackClick={OnBackClick}
        refreshView={props.refreshView}
      />
    );
  }

  return (
    <React.Fragment>
      {renderedComponent}
      <AlertDialog
        open={openModal}
        openDialog={openDialog}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        locationName={reductionMeasureName}
        confirmationMessage={ConfirmationMessages.reductionMeasureConfirmation}
      />
      <SnackBar
        show={success.show}
        message={success.message}
        severity={success.severity}
        onclose={() =>
          handleCloseSnackBar({ show: false, severity: "info", message: "" })
        }
      />
      <FormDialog
        data-testid="alert-dialog-create-new"
        dialogOpen={dialogOpen}
        hideDialogHandler={hideDialogHandlernew}
        dialogTitle="Bulk Upload Reduction Measures"
        isFullScreenAllowed={true}
        maxWidth="md"
        childComponent={
          <UploadMeasure
            closeDialog={hideDialogHandlernew}
            getReductionMeasureListByLocationId={getReductionMeasureListByLocationId}
            showSnackbar={handleCloseSnackBar}
          />
        }
      ></FormDialog>
    </React.Fragment>
  );
}
